import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { propTypes as linkPropTypes } from '../../01_atoms/Link';
import Button from '../../01_atoms/Button';
import withCookieConsentPopupFeed from './withCookieConsentPopupFeed';
import styles from './index.module.scss';

const TiktokFeedWithCookieConsent = withCookieConsentPopupFeed((props) => (
  <TiktokFeed {...props} />
));

const TiktokFeed = ({ widgetId, feedType }) => {
  const ref = useRef(null);

  const removeEventListeners = () => {
    // When widget script executed it set a few event listeners like
    // `jQuery(document).on('click', '.sk-ww-tiktok-hashtag-feed ...`
    // which remains active after leaving page with widget. If users
    // go another page with the widget instance, these listeners
    // will be set one more time, and it will break the work of the popup.
    // The listeners added by jQuery as anonymous function, and there is no
    // other way to unset them apart from fighting them by their weapon.
    if (typeof window !== 'undefined' && typeof window.jQuery !== 'undefined') {
      window.jQuery(document).unbind('click');
    }
  };

  useEffect(() => {
    // The main point of this effect is getting fresh instance of the widget.
    // If we would just place script tag, it will fire only on first visit
    // of the page with widget.
    const script = document.createElement('script');
    script.src =
      feedType == 'profile_feed'
        ? 'https://widgets.sociablekit.com/tiktok-feed/widget.js'
        : 'https://widgets.sociablekit.com/tiktok-hashtag-feed/widget.js';
    script.async = true;
    script.defer = true;

    document.body.appendChild(script);

    // Sometimes, after widget popup is closed, the page jumps to the top.
    // It's happening because popup library (magnificPopup) focuses last active
    // element, see line 446 of
    // https://widgets.sociablekit.com/libs/magnific-popup/jquery.magnific-popup.js
    // If user came by click on menu item and don't click on anything apart from
    // the widget, last active element will be "Skip to main content" link.
    // This function reset active element to the default one - body tag.
    const handleWidgetClick = () => {
      setTimeout(() => document.activeElement.blur(), 500);
    };

    const element = ref.current;
    element.addEventListener('click', handleWidgetClick);

    return () => {
      document.body.removeChild(script);
      removeEventListeners();
      element.removeEventListener('click', handleWidgetClick);
    };
  }, []);

  const className = feedType === 'profile_feed' ? 'sk-tiktok-feed' : 'sk-ww-tiktok-hashtag-feed';

  return <div className={className} ref={ref} data-embed-id={widgetId}></div>;
};

const TiktokSociablekit = ({ logo, account, hashtag, widgetId, feedType, ...props }) => {
  return (
    <div className={styles['tiktok-sociablekit']}>
      <div className={styles['tiktok-sociablekit__header']}>
        <div className={styles['tiktok-sociablekit__account-info']}>
          <div className={styles['tiktok-sociablekit__account-logo']}>
            <img src={logo.url} alt={logo.alt} width={60} height={60} />
          </div>
          <div className={styles['tiktok-sociablekit__hashtag']}>{hashtag}</div>
          <div className={styles['tiktok-sociablekit__account-name']}>{account.label}</div>
        </div>

        <Button
          tag="a"
          type="secondary"
          href={account.nextLink.url}
          isExternal
          target="_blank"
          className={styles['tiktok-sociablekit__account-cta']}
        >
          Follow us on TikTok
        </Button>
      </div>
      <div className={styles['tiktok-sociablekit__widget']}>
        <TiktokFeedWithCookieConsent widgetId={widgetId} feedType={feedType} {...props} />
      </div>

      <Button
        tag="a"
        type="secondary"
        href={account.nextLink.url}
        isExternal
        isBlock
        target="_blank"
        className={styles['tiktok-sociablekit__account-cta--mobile']}
      >
        Follow us on TikTok
      </Button>
    </div>
  );
};

TiktokFeed.propTypes = {
  widgetId: PropTypes.string.isRequired,
  feedType: PropTypes.string.isRequired,
};

const propTypes = {
  logo: PropTypes.shape({
    url: PropTypes.string,
    alt: PropTypes.string,
  }).isRequired,
  account: PropTypes.shape({
    label: PropTypes.string.isRequired,
    nextLink: PropTypes.shape(linkPropTypes).isRequired,
  }).isRequired,
  hashtag: PropTypes.string.isRequired,
  widgetId: PropTypes.string.isRequired,
  feedType: PropTypes.string.isRequired,
};

TiktokSociablekit.propTypes = propTypes;

export { propTypes };

export default TiktokSociablekit;
